import { useState, useEffect } from 'react';
import { X, Copy } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';

interface Code {
  code: string;
  privateLink: string;
  used: boolean;
  createdAt: string;
  note: string;
  usedAt?: string;
}

interface EditCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  code: Code;
}

const EditCodeModal = ({ isOpen, onClose, onSuccess, code }: EditCodeModalProps) => {
  const { toast } = useToast();
  const { getToken } = useAuth();
  const [note, setNote] = useState(code.note);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setNote(code.note);
  }, [code]);

  if (!isOpen) return null;

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(code.code);
      toast({
        title: 'Copied!',
        description: `Code "${code.code}" copied to clipboard`,
      });
    } catch (error) {
      console.error('Error copying code:', error);
      toast({
        title: 'Error',
        description: 'Failed to copy code',
        variant: 'destructive',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);

    try {
      const apiUrl = import.meta.env.PROD
        ? `/api/admin/codes/${code.code}`
        : `http://localhost:3000/api/admin/codes/${code.code}`;

      const token = await getToken();
      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ note }),
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Note updated successfully',
        });
        onSuccess();
        onClose();
      } else {
        const data = await response.json();
        toast({
          title: 'Error',
          description: data.error || 'Failed to update note',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error updating note:', error);
      toast({
        title: 'Error',
        description: 'Failed to update note',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl p-6">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-2xl font-bold text-[#006B5F] mb-6">Edit Code Note</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="code-display">Code</Label>
            <div className="relative mt-1">
              <Input
                id="code-display"
                value={code.code}
                disabled
                className="bg-gray-100 font-mono font-bold pr-12"
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={handleCopyCode}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                title="Copy code"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Code cannot be changed</p>
          </div>

          <div>
            <Label htmlFor="privateLink">Private Link</Label>
            <Input
              id="privateLink"
              value={code.privateLink}
              disabled
              className="mt-1 bg-gray-100 font-mono text-xs"
            />
            <p className="text-xs text-gray-500 mt-1">Link cannot be changed (single-use)</p>
          </div>

          <div>
            <Label htmlFor="note">Note</Label>
            <Input
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g., Fru Johnson: Skilsmisse hjælp"
              className="mt-1"
              disabled={isSubmitting}
            />
            <p className="text-xs text-gray-500 mt-1">Internal note to identify this code</p>
          </div>

          <div className="bg-gray-50 p-3 rounded-md">
            <p className="text-sm text-gray-600">
              <strong>Status:</strong> {code.used ? '🔴 Used' : '🟢 Assigned'}
            </p>
            {code.usedAt && (
              <p className="text-sm text-gray-600 mt-1">
                <strong>Used at:</strong> {new Date(code.usedAt).toLocaleString()}
              </p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-[#006B5F] hover:bg-[#005A4F]"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Updating...' : 'Update Note'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCodeModal;

